import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUIState } from '../contexts/UIStateContext';
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { useKnowledgeData } from '../hooks/useKnowledgeData';
import { useAIHealth } from '../hooks/useAIHealth';
import { api } from '../lib/api-client';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import ConversationSidebar from '../components/ConversationSidebar';
import ChatBubble from '../components/ChatBubble';
import AIResponseDetailsModal from '../components/AIResponseDetailsModal';
import SendButton from '../components/SendButton';
import FloatingActionMenu from '../components/shared/FloatingActionMenu';
import CreateProductModal from '../components/knowledge/CreateProductModal';
import NoProductsBanner from '../components/knowledge/NoProductsBanner';
import toast from '../utils/toast';

export default function Home() {
  const navigate = useNavigate();
  const { conversationId: urlConversationId } = useParams();
  const { getChatLastConversation, setChatLastConversation } = useUIState();
  const [message, setMessage] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth <= 980);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMetadata, setSelectedMetadata] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showNoProductsModal, setShowNoProductsModal] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const { user } = useAuth();

  /**
   * Knowledge data hook - Provides collections and products for the FloatingActionMenu.
   *
   * This hook centralizes the data fetching logic that's shared between Home.jsx and Knowledge.jsx.
   * It provides:
   * - collections: Array of collection objects
   * - products: Array of product objects
   * - fetchCollections(): Refresh collections after creation/update
   * - fetchProducts(): Refresh products after creation/update
   *
   * fetchOnMount is false because we only need this data when there's no active conversation
   * (when the FloatingActionMenu is visible).
   */
  const { collections, products, fetchCollections, fetchProducts } = useKnowledgeData({
    fetchOnMount: false
  });

  /**
   * AI Health check hook - Checks for AI connection issues and creates notifications.
   * This runs on mount to proactively notify users of configuration problems.
   */
  useAIHealth({ checkOnMount: true });

  // Restore last conversation if no URL conversation ID
  useEffect(() => {
    if (!urlConversationId) {
      const lastConversationId = getChatLastConversation();
      if (lastConversationId) {
        navigate(`/home/${lastConversationId}`, { replace: true });
      }
    }
  }, [urlConversationId, getChatLastConversation, navigate]);

  // Persist current conversation ID
  useEffect(() => {
    if (urlConversationId) {
      setChatLastConversation(urlConversationId);
    }
  }, [urlConversationId, setChatLastConversation]);

  const { currentConversation, sendMessage, loadMoreMessages, isConnected, isInitialConnection, error: wsError, reconnect } = useChatWebSocket(urlConversationId || null);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for "no products" error - show Create Product modal
  useEffect(() => {
    const handleNoProductsError = (event) => {
      setShowNoProductsModal(true);
      // Note: Don't show toast here - modal is enough UX feedback
    };

    window.addEventListener('noProductsError', handleNoProductsError);
    return () => window.removeEventListener('noProductsError', handleNoProductsError);
  }, []);

  // Sidebar configuration - responsive mode
  const sidebarWidth = 64;
  const sidebarMode = windowWidth <= 980 ? 'overlay' : 'push';
  const sidebarWidthPx = sidebarWidth * 4;

  // Fetch knowledge data when no conversation (for FloatingActionMenu)
  useEffect(() => {
    if (currentConversation.messages.length === 0) {
      fetchCollections();
      fetchProducts();
    }
  }, [currentConversation.messages.length, fetchCollections, fetchProducts]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentConversation.messages, currentConversation.isTyping]);

  // Infinite scroll: Load more messages when scrolling near top
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // If scrolled within 100px of top and more messages available
      if (container.scrollTop < 100 && currentConversation.hasMore && !currentConversation.isLoading) {
        const previousScrollHeight = container.scrollHeight;
        const previousScrollTop = container.scrollTop;

        loadMoreMessages();

        // After new messages load, maintain scroll position
        setTimeout(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = previousScrollTop + (newScrollHeight - previousScrollHeight);
        }, 100);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentConversation.hasMore, currentConversation.isLoading, loadMoreMessages]);

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (!message.trim() || !isConnected) return;

    const success = sendMessage(message);
    if (success) {
      setMessage('');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion);
  };

  const handleShowDetails = (messageText, metadata) => {
    setSelectedMessage(messageText);
    setSelectedMetadata(metadata);
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setSelectedMessage(null);
    setSelectedMetadata(null);
  };


  const handleRedo = (messageIndex) => {
    const userMessageIndex = messageIndex - 1;
    if (userMessageIndex >= 0 && currentConversation.messages[userMessageIndex]) {
      const userMessage = currentConversation.messages[userMessageIndex].content;
      if (userMessage && isConnected) {
        sendMessage(userMessage);
      }
    }
  };

  const handleFeedback = async (messageIndex, feedbackType) => {
    // Only allow feedback if we have a conversation ID
    if (!urlConversationId) {
      toast.error('Cannot submit feedback for unsaved conversation');
      return;
    }

    try {
      await api.post('/api/v1/chat/feedback', {
        conversation_id: urlConversationId,
        message_index: messageIndex,
        feedback: feedbackType
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--color-gradient-from) 0%, var(--color-gradient-via) 50%, var(--color-gradient-to) 100%)' }}>
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(var(--color-border-subtle) 1px, transparent 1px), linear-gradient(90deg, var(--color-border-subtle) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        opacity: 0.3
      }}></div>

      {/* Animated background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--color-accent-primary)', opacity: 0.08 }}></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--color-info)', opacity: 0.08 }}></div>
      </div>

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        width={sidebarWidth}
        mode={sidebarMode}
        autoCollapseOnOverlay={true}
      >
        <ConversationSidebar
          isCollapsed={isSidebarCollapsed}
          products={products}
          onCreateProduct={() => setShowNoProductsModal(true)}
        />
      </Sidebar>

      {/* Header */}
      <Header
        isSidebarCollapsed={isSidebarCollapsed}
        sidebarWidth={sidebarWidth}
        sidebarMode={sidebarMode}
      />

      {/* Main Content */}
      <main
        className={`fixed top-16 right-0 bottom-0 transition-all duration-300 ${
          currentConversation.messages.length === 0 ? 'flex items-center justify-center p-8' : 'p-0'
        }`}
        style={{
          left: sidebarMode === 'overlay' || isSidebarCollapsed ? '0' : `${sidebarWidthPx}px`
        }}
      >
        <div className={`w-full relative z-10 transition-all duration-500 ${
          currentConversation.messages.length === 0 ? 'max-w-3xl' : 'h-full'
        }`}>
          {currentConversation.messages.length === 0 ? (
            /* Welcome Screen - Centered */
            <div className="text-center">
              {/* Welcome Message */}
              <div className="mb-8">
                <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent mb-4 animate-fade-in-up" style={{ color: 'var(--color-text-on-accent)' }}>
                                  Welcome back, {user?.username}!
                </h1>
                <p className="text-gray-400 text-lg animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                  How can I help you today?
                </p>
              </div>

              {/* Connection Status */}
              {!isConnected && !isInitialConnection && (
                <div className="mb-4 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-sm text-yellow-400">Connecting to server...</p>
                </div>
              )}

              {/* Chat Input */}
              <div className="relative group animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {/* Gradient border glow */}
                <div className="absolute -inset-0.5 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000" style={{ background: 'linear-gradient(90deg, var(--color-gradient-accent-from), var(--color-gradient-accent-to))' }}></div>

                {/* Input Container */}
                <div className="relative rounded-2xl shadow-2xl p-6" style={{
                  background: 'linear-gradient(135deg, var(--color-bg-secondary), var(--color-bg-primary))',
                  borderWidth: '1px',
                  borderColor: 'var(--color-border-default)'
                }}>
                  <form onSubmit={handleSendMessage}>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Ask me anything..."
                          className="w-full px-5 py-4 border-2 rounded-xl text-base focus:outline-none transition-all duration-200"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border-default)',
                            color: 'var(--color-text-primary)',
                            paddingRight: currentConversation.product ? '140px' : undefined
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
                            e.currentTarget.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'var(--color-border-default)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          disabled={!isConnected}
                        />
                        {currentConversation.product && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 bg-purple-600/20 border border-purple-500/30 rounded-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                                                      <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            <span className="text-sm font-medium text-purple-300">{currentConversation.product.name}</span>
                          </div>
                        )}
                      </div>
                      <SendButton disabled={!isConnected || !message.trim()} />
                    </div>
                  </form>
                </div>
              </div>

              {/* Suggestions */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <button
                  onClick={() => handleSuggestionClick("What can you help me with?")}
                  className="px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-all text-left"
                >
                  <span className="block font-semibold mb-1">Explore features</span>
                  <span className="text-gray-500">What can you help me with?</span>
                </button>
                <button
                  onClick={() => handleSuggestionClick("How does this system work?")}
                  className="px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-all text-left"
                >
                  <span className="block font-semibold mb-1">Learn more</span>
                  <span className="text-gray-500">How does this system work?</span>
                </button>
              </div>
            </div>
          ) : (
            /* Chat Messages View - Full Height with Overlay Input */
            <div className="h-full flex flex-col relative">
              {/* Subtle Loading Indicator - Top Right Corner */}
              {currentConversation.isLoading && (
                <div className="absolute top-6 right-6 z-10 px-3 py-1.5 bg-gray-800/90 border border-gray-700 rounded-lg shadow-lg flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-400">Loading conversation...</span>
                </div>
              )}

              {/* Messages Container - Full height with bottom padding */}
              <div ref={messagesContainerRef} className="absolute inset-0 overflow-y-auto scroll-smooth pb-32">
                <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
                  {/* Connection Status */}
                  {!isConnected && !isInitialConnection && (
                    <div className="px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-sm text-yellow-400">Reconnecting to server...</p>
                    </div>
                  )}

                  {currentConversation.messages.map((msg, idx) => (
                    <ChatBubble
                      key={idx}
                      message={msg.content}
                      isUser={msg.role === 'user'}
                      timestamp={msg.timestamp}
                      metadata={msg.metadata}
                      messageIndex={idx}
                      conversationId={urlConversationId}
                      onShowDetails={handleShowDetails}
                      onRedo={handleRedo}
                      onFeedback={handleFeedback}
                    />
                  ))}

                  {/* Typing Indicator */}
                  {currentConversation.isTyping && (
                    <div className="flex justify-start animate-fade-in-up">
                      <div className="max-w-2xl px-5 py-3 rounded-2xl bg-gray-800 border border-gray-700">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input at Bottom - Fixed Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none">
                <div className="max-w-3xl mx-auto pointer-events-auto">
                  <div className="relative group">
                    <div className="absolute -inset-0.5 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000" style={{ background: 'linear-gradient(90deg, var(--color-gradient-accent-from), var(--color-gradient-accent-to))' }}></div>
                    <div className="relative backdrop-blur-sm rounded-2xl shadow-2xl p-4" style={{
                      background: 'linear-gradient(135deg, var(--color-bg-secondary), var(--color-bg-primary))',
                      borderWidth: '1px',
                      borderColor: 'var(--color-border-default)',
                      opacity: 0.95
                    }}>
                      <form onSubmit={handleSendMessage}>
                        <div className="flex items-center space-x-4">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              placeholder="Type your message..."
                              className="w-full px-5 py-3 border-2 rounded-xl text-base focus:outline-none transition-all duration-200"
                              style={{
                                backgroundColor: 'var(--color-bg-primary)',
                                borderColor: 'var(--color-border-default)',
                                color: 'var(--color-text-primary)',
                                paddingRight: currentConversation.product ? '140px' : undefined
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
                                e.currentTarget.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = 'var(--color-border-default)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              disabled={!isConnected}
                            />
                            {currentConversation.product && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 bg-purple-600/20 border border-purple-500/30 rounded-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                                                              <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                <span className="text-sm font-medium text-purple-300">{currentConversation.product.name}</span>
                              </div>
                            )}
                          </div>
                          <SendButton disabled={!isConnected || !message.trim()} />
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Floating Action Button - Knowledge actions (only when no conversation) */}
      {currentConversation.messages.length === 0 && (
        <FloatingActionMenu
          collections={collections}
          products={products}
          onRefreshProducts={fetchProducts}
          onRefreshCollections={fetchCollections}
        />
      )}

      {/* AI Response Details Modal */}
      <AIResponseDetailsModal
        isOpen={detailsModalOpen}
        onClose={handleCloseDetails}
        message={selectedMessage}
        metadata={selectedMetadata}
      />

      {/* No Products Modal - Shown when user tries to chat without any products */}
      <CreateProductModal
        isOpen={showNoProductsModal}
        onClose={() => setShowNoProductsModal(false)}
        onProductCreated={(newProduct) => {
          setShowNoProductsModal(false);
          fetchProducts();
          toast.success('Product created! You can now start chatting.');
        }}
        triggeredFrom="chat"
      />
    </div>
  );
}
